import React, { useCallback, useEffect } from 'react';

import CSSModule from 'react-css-modules';
import { Users } from 'react-feather';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';

import { Spinner } from 'components';

import messages from './messages';
import styles from './Referrals.module.scss';

/**
 * Shared list renderer for BusinessesList / CustomersList — both display the
 * same shape of paginated referral data (name + membership expiry).
 */
const CommonList = ({ getListAction, stateEntity }) => {
  const dispatch = useDispatch();
  const intl = useIntl();

  const { fetching, fetched, data, error } = stateEntity;
  const { results: list, nextPage } = data || { results: [] };

  const getReferrals = useCallback(
    params => {
      dispatch(getListAction(params));
    },
    [dispatch, getListAction],
  );

  useEffect(() => {
    getReferrals();
  }, [getReferrals]);

  const handleLoadMore = () => {
    if (nextPage) {
      getReferrals({ page: nextPage });
    }
  };

  const expiresBadge = expiresAt => {
    const parsedDate = expiresAt && new Date(expiresAt);
    return parsedDate ? intl.formatMessage(messages.expiresBadge, { date: parsedDate }) : null;
  };

  if (error) {
    return (
      <div styleName="page">
        <p styleName="state-message">Something went wrong loading this list.</p>
      </div>
    );
  }

  if (!list || (fetching && !fetched)) {
    return (
      <div styleName="page" style={{ display: 'flex', justifyContent: 'center' }}>
        <Spinner show size="lg" />
      </div>
    );
  }

  if (list.length === 0) {
    return (
      <div styleName="page">
        <div styleName="empty">
          <Users styleName="empty-icon" />
          <h2 styleName="empty-title">{intl.formatMessage(messages.emptyTitle)}</h2>
          <p styleName="empty-text">{intl.formatMessage(messages.emptyText)}</p>
          <Link to="/my-account" styleName="empty-link">
            {intl.formatMessage(messages.backToAccount)}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div styleName="page">
      <div styleName="list">
        {list.map(item => (
          <div key={`${item.name}-${item.membership_expired}`} styleName="tile">
            <span styleName="name-text">{item.name}</span>
            <span styleName="subtitle-text">{expiresBadge(item.membership_expired)}</span>
          </div>
        ))}
      </div>

      {nextPage && (
        <div styleName="load-more-row">
          <button type="button" styleName="load-more-btn" onClick={handleLoadMore} disabled={fetching}>
            {fetching ? <Spinner show size="sm" /> : 'Load more'}
          </button>
        </div>
      )}
    </div>
  );
};

export default CSSModule(CommonList, styles, { allowMultiple: true });